/**
 * "Open *Fix and re-solve* for this absence" (#1960).
 *
 * The flow has three doors — the ladder's out-sick footer (inside the
 * roster grid), the Review cover inbox, and since #2029 the standing button
 * on the workspace toolbar — and one
 * panel, mounted once by the app shell (`<FixAndResolveHost/>`) in the
 * right-panel overlay. The ladder closes as the panel opens (one drawer
 * slot), so the request carries everything the panel needs: the team, the
 * cycle whose day it is, the day, and who is absent on which dates (they
 * are pinned off before the lead adds anything).
 */
import { create } from "zustand";

export interface FixAndResolveAbsent {
  membership_id: string;
  display_name: string;
  /** ISO dates of the absence (the MC window, clamped to the cycle). */
  dates: string[];
}

export interface FixAndResolveRequest {
  teamId: string;
  cycleId: string;
  /** ISO date the flow starts from (the ladder's day). */
  date: string;
  absent: FixAndResolveAbsent[];
  /**
   * #2029 — the dates to re-solve, when the door names them: the toolbar's
   * standing *Fix and re-solve* opens on the dates on screen (step 2 starts
   * on *Custom* with these). Omitted = the team's days from the first pin.
   */
  dates?: { from: string; to: string } | undefined;
  /**
   * #2715 - *Offer the move* from a held-move card: the stand-in the card
   * suggests, pinned on the holder's seat when the panel opens.
   */
  seatPin?:
    | {
        membership_id: string;
        display_name: string;
        date: string;
        station: string;
        session: string;
      }
    | undefined;
  /** #2715 - keep the change as a draft until Publish (the apply holds it). */
  holdForPublish?: boolean | undefined;
  /** #2715 - the holder's standing pin; the apply records the cover on it. */
  coverFor?: string | undefined;
}

interface FixAndResolveState {
  request: FixAndResolveRequest | null;
  openFixAndResolve: (r: FixAndResolveRequest) => void;
  closeFixAndResolve: () => void;
}

export const useFixAndResolveStore = create<FixAndResolveState>((set) => ({
  request: null,
  openFixAndResolve: (r) => set({ request: r }),
  closeFixAndResolve: () => set({ request: null }),
}));
